import { Member } from '@ui/interfaces'
import axios from '@ui/lib/api/axiosHelper'
import { getQueryRailsHeaders } from './queryRailsUtil'

/*
************************************************************
CONSTANTS
************************************************************
*/
const PIPELINES_URL = '/ui/pipelines'
const USER_PERMISSIONS_URL = '/ui/user_permissions'
const TEAM_PERMISSIONS_URL = '/ui/team_permissions'

/*
************************************************************
TYPES
************************************************************
*/
interface Pipeline {
  id: string
  name: string
  enabled: boolean
  is_primary: boolean
  created_by: string
  created_timestamp: string
  last_updated_by: string
  last_updated_timestamp: string
}

export interface PipelineWithPermissions extends Pipeline {
  team_permissions: TeamPermission[]
  user_permissions: UserPermission[]
}

export interface TeamPermission {
  id: number
  pipeline_id: string
  team_id: string
  action: string
  created_at: string
  updated_at: string
}

export interface UserPermission {
  id: number
  pipeline_id: string
  user_id: number
  action: string
  created_at: string
  updated_at: string
}

interface CreatePipelineInput {
  name: string
  enabled: boolean
}

/*
************************************************************
SERVICES
************************************************************
*/

export const pipelineService = {
  getPipelines: async (member: Member): Promise<Pipeline[]> => {
    const response = await axios.get(PIPELINES_URL, { headers: getQueryRailsHeaders(member) })
    return response.data.data
  },
  getPipeline: async (pipelineId: string, member: Member): Promise<PipelineWithPermissions> => {
    const response = await axios.get(`${PIPELINES_URL}/${pipelineId}`, { headers: getQueryRailsHeaders(member) })
    return response.data.data
  },
  createPipeline: async (input: CreatePipelineInput, member: Member): Promise<Pipeline> => {
    const response = await axios.post(PIPELINES_URL, { pipeline: input }, { headers: getQueryRailsHeaders(member) })
    return response.data.data
  },
  inviteUser: async (pipelineId: string, userId: number, member: Member): Promise<UserPermission> => {
    const response = await axios.post(
      `${USER_PERMISSIONS_URL}`,
      { user_permission: { pipeline_id: pipelineId, user_id: userId } },
      { headers: getQueryRailsHeaders(member) }
    )
    return response.data.data
  },
  removeUser: async (userPermissionId: number, member: Member): Promise<void> => {
    const response = await axios.delete(`${USER_PERMISSIONS_URL}/${userPermissionId}`, { headers: getQueryRailsHeaders(member) })
    return response.data.data
  },
  inviteTeam: async (pipelineId: string, teamId: string, member: Member): Promise<TeamPermission> => {
    const response = await axios.post(
      `${TEAM_PERMISSIONS_URL}`,
      {
        team_permission: { pipeline_id: pipelineId, team_id: teamId },
      },
      { headers: getQueryRailsHeaders(member) }
    )
    return response.data.data
  },
  removeTeam: async (teamPermissionId: number, member: Member): Promise<void> => {
    const response = await axios.delete(`${TEAM_PERMISSIONS_URL}/${teamPermissionId}`, { headers: getQueryRailsHeaders(member) })
    return response.data.data
  },
}
